import { ApiTwoTone, ControlOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useSubscriptionQueries } from '@client/queries/useSubscriptionQueries';
import { useDrawerActions } from '@client/store/drawer.store';
import { useLoading } from '@client/store/loading.store';

import styles from '../../features.module.less';

import { SubscriptionsDrawerContent } from './SubscriptionsDrawerContent';

type TSubscriptionsDrawerButtonProps = {
  disabled?: boolean;
};

export const SubscriptionsDrawerButton: React.FC<
  TSubscriptionsDrawerButtonProps
> = ({ disabled }) => {
  const { setOpenDrawer, setConfigDrawer, setOnRefreshDrawer } =
    useDrawerActions();
  const { ability } = useAuth();
  const { t } = useTranslation();
  const { useActiveSubscriptions } = useSubscriptionQueries();

  const { refetch, isLoading, isFetching } = useActiveSubscriptions();

  const { loading } = useLoading(isLoading || isFetching);

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  useEffect(() => {
    // Set up the refresh function for the drawer
    setOnRefreshDrawer(handleRefresh);
  }, [handleRefresh, setOnRefreshDrawer]);

  const handleShowSubscriptions = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setOpenDrawer(true);
    setConfigDrawer({
      size: 600,
      children: <SubscriptionsDrawerContent />,
      title: (
        <div className={styles.chipLabel}>
          <ControlOutlined />
          {t('subscriptions')}
        </div>
      ),
    });
  };

  return (
    <Button
      loading={loading}
      disabled={
        disabled ||
        ability.cannot(
          EAbilityActions.ASSIGN,
          `${EAbilitySubjects.FEATURE}.${EAbilitySubjects.SUBSCRIPTIONS}`,
        )
      }
      icon={<ApiTwoTone />}
      onClick={handleShowSubscriptions}
    >
      {t('actions.show', { type: t('subscriptions') })}
    </Button>
  );
};
